<template>
  <div class="review-manager">
    <header class="manager-header">
      <div>
        <div class="eyebrow">Manuelle Evaluation</div>
        <h2>Praxis- und Video-Abgaben prüfen</h2>
        <p>Öffne eingereichte PDFs oder Videos und gib direkt ein nachvollziehbares Feedback.</p>
      </div>
      <v-btn icon class="refresh-action" aria-label="Abgaben aktualisieren" :loading="loading" @click="loadSubmissions"
        ><v-icon>mdi-refresh</v-icon></v-btn
      >
    </header>

    <nav class="filter-tabs" aria-label="Abgaben filtern">
      <button
        v-for="filter in filters"
        :key="filter.value"
        type="button"
        :class="{ active: statusFilter === filter.value }"
        @click="setFilter(filter.value)">
        {{ filter.label }}
      </button>
    </nav>

    <div v-if="loading" class="submission-grid">
      <v-skeleton-loader v-for="index in 4" :key="index" dark type="list-item-avatar-three-line, actions" class="skeleton-card" />
    </div>
    <div v-else-if="submissions.length" class="submission-grid">
      <article v-for="submission in submissions" :key="submission._id" class="submission-card">
        <span class="person-icon"><v-icon>{{ submissionIcon(submission) }}</v-icon></span>
        <div class="submission-copy">
          <span>{{ moduleTitle(submission) }}</span>
          <h3>{{ userName(submission) }}</h3>
          <p>{{ userMeta(submission) }}</p>
          <small>Version {{ submission.attempt || 1 }} · Eingereicht {{ formatDate(submission.submittedAt || submission.createdAt) }}</small>
        </div>
        <span class="status-badge" :class="`status-${submission.status}`">{{ statusLabel(submission.status) }}</span>
        <v-btn outlined color="#58d388" class="review-action" @click="openReview(submission)">
          <v-icon left>{{ submission.type === "document_submission" ? "mdi-file-eye-outline" : "mdi-play-circle-outline" }}</v-icon
          >{{ submission.status === "submitted" ? "Prüfen" : "Ansehen" }}
        </v-btn>
      </article>
    </div>
    <div v-else class="empty-state">
      <span><v-icon>mdi-file-document-check-outline</v-icon></span>
      <h3>Keine Abgaben in diesem Status</h3>
      <p>Neue Praxis- und Video-Abgaben erscheinen automatisch hier.</p>
    </div>

    <v-dialog v-model="reviewDialog" max-width="62rem" dark scrollable :fullscreen="$vuetify.breakpoint.xsOnly" content-class="review-dialog">
      <v-card v-if="selected" dark class="dialog-card">
        <header class="dialog-header">
          <div>
            <div class="eyebrow">{{ moduleTitle(selected) }}</div>
            <h2>{{ userName(selected) }}</h2>
            <p>{{ userMeta(selected) }} · Version {{ selected.attempt || 1 }} · {{ formatDate(selected.submittedAt || selected.createdAt) }}</p>
          </div>
          <v-btn icon class="refresh-action" aria-label="Dialog schliessen" :disabled="saving" @click="reviewDialog = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </header>

        <v-card-text class="dialog-body">
          <section v-if="selected.type === 'document_submission'" class="document-surface">
            <SecuredMedia
              :src="documentPath(selected)"
              media-type="pdf"
              :download-name="selected.mediaFile && selected.mediaFile.originalName"
              eager
              :cache="false"
              :cache-key="`practice-review-${selected._id}`" />
            <v-btn outlined color="#58d388" class="document-download" :loading="downloading" @click="downloadDocument(selected)">
              <v-icon left>mdi-download-outline</v-icon>PDF herunterladen
            </v-btn>
          </section>
          <section v-else class="video-surface">
            <SecuredMedia
              :src="videoPath(selected)"
              media-type="video"
              stream-playback
              eager
              :cache="false"
              :cache-key="`leadership-review-${selected._id}`" />
          </section>

          <section class="feedback-section">
            <div class="feedback-heading">
              <span><v-icon>mdi-comment-edit-outline</v-icon></span>
              <div>
                <h3>Feedback</h3>
                <p>Das Feedback wird der einreichenden Person direkt bei ihrer Abgabe angezeigt.</p>
              </div>
            </div>
            <v-textarea
              v-model="feedback"
              label="Persönliches Feedback"
              outlined
              rows="5"
              counter="2000"
              :disabled="saving"
              :rules="[(value) => !value || value.length <= 2000 || 'Maximal 2000 Zeichen']" />
            <v-alert v-if="error" type="error" outlined text>{{ error }}</v-alert>
          </section>
        </v-card-text>

        <v-card-actions v-if="canReviewSelected" class="dialog-actions">
          <v-btn
            outlined
            color="#ff8d98"
            class="decision-action"
            :loading="saving === 'revision_required'"
            :disabled="Boolean(saving)"
            @click="review('revision_required')">
            <v-icon left>mdi-reload</v-icon>Überarbeitung anfordern
          </v-btn>
          <v-spacer />
          <v-btn
            color="#009640"
            depressed
            class="decision-action"
            :loading="saving === 'approved'"
            :disabled="Boolean(saving)"
            @click="review('approved')">
            <v-icon left>mdi-check-decagram-outline</v-icon>Als bestanden markieren
          </v-btn>
        </v-card-actions>
        <v-card-actions v-else class="dialog-actions readonly-actions">
          <span><v-icon>mdi-lock-check-outline</v-icon>Dieser Entscheid ist abgeschlossen und nur noch lesbar.</span>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3500" top right>{{ snackbar.text }}</v-snackbar>
  </div>
</template>

<script>
import api from "@/api"
import SecuredMedia from "../SecuredMedia.vue"

function unwrapList(result) {
  if (Array.isArray(result)) return result
  return (result && (result.submissions || result.items)) || []
}

export default {
  name: "LeadershipReviewManager",
  components: { SecuredMedia },
  data() {
    return {
      filters: [
        { label: "Offen", value: "submitted" },
        { label: "Überarbeitung", value: "revision_required" },
        { label: "Bestanden", value: "approved" },
      ],
      statusFilter: "submitted",
      submissions: [],
      loading: false,
      reviewDialog: false,
      selected: null,
      feedback: "",
      saving: "",
      downloading: false,
      error: "",
      snackbar: { show: false, color: "success", text: "" },
    }
  },
  computed: {
    canReviewSelected() {
      return Boolean(this.selected && ["submitted", "in_review"].includes(this.selected.status))
    },
  },
  mounted() {
    this.loadSubmissions()
  },
  watch: {
    reviewDialog(isOpen) {
      if (!isOpen) this.$nextTick(() => (this.selected = null))
    },
  },
  methods: {
    notify(color, text) {
      this.snackbar = { show: true, color, text }
    },
    async loadSubmissions() {
      this.loading = true
      try {
        const result = await api.akademie.fetchReviewSubmissions({ status: this.statusFilter })
        this.submissions = unwrapList(result)
      } catch (error) {
        this.submissions = []
        this.notify("error", "Die Abgaben konnten nicht geladen werden.")
      } finally {
        this.loading = false
      }
    },
    setFilter(status) {
      this.statusFilter = status
      this.loadSubmissions()
    },
    openReview(submission) {
      this.selected = submission
      this.feedback = submission.feedback || ""
      this.error = ""
      this.reviewDialog = true
    },
    async review(status) {
      if (status === "revision_required" && !this.feedback.trim()) {
        this.error = "Bitte gib an, was überarbeitet werden soll."
        return
      }
      this.saving = status
      this.error = ""
      try {
        await api.akademie.reviewSubmission(this.selected._id, {
          status,
          feedback: this.feedback.trim(),
        })
        this.reviewDialog = false
        this.notify("success", status === "approved" ? "Die Evaluation ist bestanden." : "Die Überarbeitung wurde angefordert.")
        await this.loadSubmissions()
      } catch (error) {
        this.error =
          (error.response && error.response.data && (error.response.data.error || error.response.data.message)) ||
          "Die Beurteilung konnte nicht gespeichert werden."
      } finally {
        this.saving = ""
      }
    },
    videoPath(submission) {
      return api.akademie.getLeadershipSubmissionPlaybackPath(submission._id)
    },
    documentPath(submission) {
      return api.akademie.getSubmissionFilePath(submission._id)
    },
    submissionIcon(submission) {
      return submission.type === "document_submission" ? "mdi-file-account-outline" : "mdi-video-account"
    },
    async downloadDocument(submission) {
      if (this.downloading) return
      this.downloading = true
      try {
        await api.akademie.downloadFile(
          `${this.documentPath(submission)}?download=1`,
          (submission.mediaFile && submission.mediaFile.originalName) || "Praxisaufgabe.pdf",
        )
      } catch (error) {
        this.error = "Die PDF-Datei konnte nicht geöffnet werden."
      } finally {
        this.downloading = false
      }
    },
    moduleTitle(submission) {
      const moduleItem = submission.module || submission.moduleId || {}
      return typeof moduleItem === "string" ? "Akademie-Modul" : moduleItem.title || "Akademie-Modul"
    },
    userName(submission) {
      const user = submission.user || submission.userId || {}
      if (typeof user === "string") return "Mitarbeitende Person"
      return [user.vorname || user.firstName, user.nachname || user.lastName].filter(Boolean).join(" ") || user.name || "Mitarbeitende Person"
    },
    userMeta(submission) {
      const user = submission.user || submission.userId || {}
      const studio = user.studio_location || user.studio || submission.center || ""
      const center = typeof studio === "string" ? studio : studio.name || studio.studioName || ""
      const role = user.function || user.funktion || user.position || submission.function || ""
      const track = submission.module && submission.module.track === "leadership" ? "Leadership" : "Learning Suite"
      return [center, role, track].filter(Boolean).join(" · ")
    },
    statusLabel(status) {
      const labels = { submitted: "Offen", revision_required: "Überarbeitung", approved: "Bestanden" }
      return labels[status] || "Offen"
    },
    formatDate(date) {
      return date ? new Date(date).toLocaleString("de-CH", { dateStyle: "medium", timeStyle: "short" }) : "-"
    },
  },
}
</script>

<style scoped>
.review-manager {
  color: #f4f4f5;
  font-size: 1rem;
}

.manager-header,
.dialog-header,
.feedback-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
}

.manager-header,
.dialog-header {
  justify-content: space-between;
}

.eyebrow {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.manager-header h2,
.dialog-header h2 {
  margin: 0.1875rem 0 0.25rem;
  color: #fff;
  font-size: 1.375rem;
  line-height: 1.3;
}

.manager-header p,
.dialog-header p,
.feedback-heading p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.refresh-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.8125rem !important;
  color: #fff !important;
  background: rgba(255, 255, 255, 0.05) !important;
}

.filter-tabs {
  display: flex;
  gap: 0.375rem;
  margin: 1rem 0;
  padding: 0.375rem;
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.12);
}

.filter-tabs button {
  min-height: 2.5rem;
  padding: 0.5rem 0.875rem;
  border: 0;
  border-radius: 0.6875rem;
  color: #a3a3a8;
  background: transparent;
  font-size: 0.875rem;
  font-weight: 750;
  white-space: nowrap;
}

.filter-tabs button.active {
  color: #fff;
  background: rgba(0, 150, 64, 0.16);
}

.submission-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.submission-card,
.skeleton-card {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
}

.submission-card {
  display: grid;
  grid-template-columns: 3rem minmax(0, 1fr) auto;
  align-items: start;
  gap: 0.75rem;
  padding: 0.875rem;
}

.person-icon {
  display: grid;
  width: 3rem;
  height: 3rem;
  place-items: center;
  border-radius: 0.875rem;
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.14);
}

.person-icon .v-icon {
  color: currentColor;
}

.submission-copy {
  min-width: 0;
}

.submission-copy > span,
.submission-copy small {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 750;
}

.submission-copy h3 {
  margin: 0.1875rem 0;
  overflow: hidden;
  color: #fff;
  font-size: 1rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.submission-copy p {
  margin: 0 0 0.25rem;
  color: #a3a3a8;
  font-size: 0.875rem;
}

.submission-copy small {
  color: #8f9095;
  font-weight: 500;
}

.status-badge {
  padding: 0.375rem 0.625rem;
  border-radius: 999px;
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.13);
  font-size: 0.75rem;
  font-weight: 750;
}

.status-approved {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.status-revision_required {
  color: #ff9aa5;
  background: rgba(244, 63, 94, 0.12);
}

.review-action {
  grid-column: 2 / -1;
  justify-self: end;
  min-height: 2.5rem;
  border-radius: 0.75rem !important;
  font-size: 0.875rem;
  font-weight: 750;
  text-transform: none;
}

.empty-state {
  display: grid;
  min-height: 16rem;
  place-items: center;
  align-content: center;
  text-align: center;
}

.empty-state > span {
  display: grid;
  width: 3.5rem;
  height: 3.5rem;
  place-items: center;
  border-radius: 1rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.empty-state > span .v-icon {
  color: currentColor;
}

.empty-state h3 {
  margin: 0.875rem 0 0.25rem;
  color: #fff;
  font-size: 1.125rem;
}

.empty-state p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
}

.dialog-card {
  color: #f4f4f5;
  background: #191919 !important;
  font-size: 1rem;
}

.dialog-header {
  padding: 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.dialog-body {
  padding: 1.25rem !important;
}

.video-surface {
  min-height: 18rem;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: #090909;
}

.document-surface {
  display: grid;
  gap: 0.75rem;
  min-height: 24rem;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: #090909;
}

.document-surface ::v-deep .secured-media-content.pdf {
  min-height: 32rem;
}

.document-download {
  justify-self: end;
  min-height: 2.75rem;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem;
  font-weight: 750;
  text-transform: none;
}

.video-surface ::v-deep video {
  max-height: 34rem;
}

.feedback-section {
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
}

.feedback-heading {
  margin-bottom: 0.875rem;
}

.feedback-heading > span {
  display: grid;
  flex: 0 0 2.5rem;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  border-radius: 0.75rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.feedback-heading > span .v-icon {
  color: currentColor;
}

.feedback-heading h3 {
  margin: 0;
  color: #fff;
  font-size: 1rem;
}

.dialog-actions {
  padding: 1rem 1.25rem !important;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.decision-action {
  min-height: 2.75rem;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem;
  font-weight: 750;
  text-transform: none;
}

.readonly-actions {
  justify-content: center;
}

.readonly-actions span {
  display: inline-flex;
  align-items: center;
  gap: 0.4375rem;
  color: #a3a3a8;
  font-size: 0.875rem;
}

.readonly-actions .v-icon {
  color: #a3a3a8;
  font-size: 1.125rem;
}

@media (max-width: 59.99rem) {
  .submission-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 37.49rem) {
  .manager-header p {
    display: none;
  }

  .submission-card {
    grid-template-columns: 3rem minmax(0, 1fr);
  }

  .status-badge {
    grid-column: 2;
    justify-self: start;
  }

  .review-action {
    grid-column: 1 / -1;
    width: 100%;
  }

  .dialog-actions {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .dialog-actions .v-spacer {
    display: none;
  }

  .decision-action {
    width: 100%;
  }
}
</style>
